import { useMemo } from 'react'

import { Modal } from '@/components/ui'
import { actionMeta } from '@/pages/audit-logs/constants'
import { formatDate, formatTime } from '@/pages/audit-logs/format'
import type { AuditFilters } from '@/pages/audit-logs/types'
import { useAuditLogs } from '@/pages/audit-logs/useAuditLogs'

import type { ManagedGeofence } from './types'

interface GeofenceHistoryModalProps {
  geofence?: ManagedGeofence
  show: boolean
  onClose: () => void
  onShowMore: () => void
}

export const GeofenceHistoryModal = ({ geofence, show, onClose, onShowMore }: GeofenceHistoryModalProps) => {
  const filters = useMemo<AuditFilters>(
    () => ({
      companyIds: geofence ? [geofence.companyId] : [],
      time: '90d',
      changeTypes: [],
      entityTypes: ['geofence'],
      userIds: [],
      entityId: geofence ? String(geofence.id) : undefined,
      search: '',
    }),
    [geofence]
  )
  const query = useAuditLogs(filters, show && !!geofence)
  const rows = [...query.rows].sort((a, b) => Date.parse(b.loggedAt) - Date.parse(a.loggedAt)).slice(0, 5)

  const bodyContent = query.isLoading ? (
    <div className="py-8 text-center text-sm text-base-content/60">Loading recent changes...</div>
  ) : query.isError ? (
    <div className="alert alert-error">
      <span>Could not load this geofence&apos;s recent changes.</span>
    </div>
  ) : rows.length === 0 ? (
    <div className="py-8 text-center text-sm text-base-content/60">No changes found in the last 90 days.</div>
  ) : (
    <div>
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-base-200 text-[11px] font-semibold uppercase tracking-wide text-base-content/60">
          <tr className="border-b border-base-300">
            <th className="w-[160px] px-3 py-2 text-left">Timestamp</th>
            <th className="w-[190px] px-3 py-2 text-left">Entity Name</th>
            <th className="w-[130px] px-3 py-2 text-left">Action</th>
            <th className="min-w-[300px] px-3 py-2 text-left">Summary</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((record) => {
            const meta = actionMeta(record.changeType)

            return (
              <tr key={record.id} className="h-14 border-b border-base-200 hover:bg-base-200">
                <td className="whitespace-nowrap px-3">
                  <div>{formatDate(record.loggedAt)}</div>
                  <div className="text-xs text-base-content/55">{formatTime(record.loggedAt)}</div>
                </td>
                <td
                  className="w-[190px] max-w-[190px] whitespace-normal break-words px-3 font-medium"
                  title={record.entityName || geofence?.name}
                >
                  <span className="line-clamp-2 break-words">{record.entityName || geofence?.name || '—'}</span>
                </td>
                <td className="px-3">
                  <span
                    className={`inline-flex items-center gap-1 rounded-field px-2 py-0.5 text-[11px] font-semibold uppercase ${meta.badgeClass}`}
                  >
                    <span aria-hidden>{meta.icon}</span>
                    {meta.label}
                  </span>
                </td>
                <td className="min-w-[280px] px-3 text-base-content/70">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate" title={record.summary}>
                      {record.summary || meta.label}
                    </span>
                    {record.changeCount > 1 && (
                      <span className="shrink-0 rounded-selector bg-base-300 px-1.5 py-0.5 text-[10px] text-base-content/70">
                        +{record.changeCount - 1} more
                      </span>
                    )}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  return (
    <Modal
      show={show && !!geofence}
      onHide={onClose}
      title={'Recent changes: ' + (geofence?.name ?? 'Geofence')}
      maxWidth="min(95vw, 1000px)"
      bodyContent={bodyContent}
      footerButtons={[
        {
          label: 'Open in Audit Logs',
          class: 'btn-primary btn-sm text-xs',
          useModalVariant: true,
          onClick: onShowMore,
        },
      ]}
    />
  )
}
